/**
 * ACT-3188: When the run-on-booking flag is ON, treat EXPIRING background checks
 * as completed so the header progress and counts exclude them.
 */
import {
  RequirementWorkflowType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";

import { type WorkerRequirement } from "../../api/types";
import { type DocumentStatusProgressVariant } from "../StatusProgress";
import { useDocumentsPageApiContext } from "../useApiProvider";

interface DocumentsPageHeaderData {
  variant: DocumentStatusProgressVariant;
  completedQualificationWorkerRequirements: WorkerRequirement[];
  totalQualificationWorkerRequirements: number;
}

export function useDocumentsPageHeader(): DocumentsPageHeaderData {
  const {
    mandatoryRequirementsQuery: { data: mandatoryRequirements },
  } = useDocumentsPageApiContext();

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  function shouldTreatExpiringBackgroundCheckAsCompleted(requirement: WorkerRequirement): boolean {
    return (
      isRunBackgroundCheckOnBookingEnabled &&
      requirement.status === WorkerRequirementStatus.EXPIRING &&
      requirement.requirement?.attributes?.requirementWorkflowType ===
        RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
    );
  }

  const missingQualificationWorkerRequirements = mandatoryRequirements?.filter((requirement) => {
    if (shouldTreatExpiringBackgroundCheckAsCompleted(requirement)) {
      return false;
    }

    return [
      WorkerRequirementStatus.MISSING,
      WorkerRequirementStatus.EXPIRED,
      WorkerRequirementStatus.EXPIRING,
      WorkerRequirementStatus.REJECTED,
    ].includes(requirement.status);
  });

  const inReviewQualificationWorkerRequirements = mandatoryRequirements?.filter(
    (requirement) => requirement.status === WorkerRequirementStatus.IN_REVIEW
  );
  const totalQualificationWorkerRequirements = mandatoryRequirements?.length ?? 0;
  const completedQualificationWorkerRequirements =
    mandatoryRequirements?.filter((requirement) => {
      if (shouldTreatExpiringBackgroundCheckAsCompleted(requirement)) {
        return true;
      }

      return requirement.status === WorkerRequirementStatus.APPROVED;
    }) ?? [];

  function getHeaderVariant(): DocumentStatusProgressVariant {
    if ((missingQualificationWorkerRequirements?.length ?? 0) > 0) {
      return "missing";
    }

    if ((inReviewQualificationWorkerRequirements?.length ?? 0) > 0) {
      return "in_review";
    }

    return "completed";
  }

  return {
    variant: getHeaderVariant(),
    completedQualificationWorkerRequirements,
    totalQualificationWorkerRequirements,
  };
}
